import React, { useMemo } from 'react';
import CalcShell from '../../components/CalcShell';
import NumberField from '../../components/NumberField';
import YesNoToggle from '../../components/YesNoToggle';
import RiskScoreResult from '../../components/RiskScoreResult';
import {
  dipssRiskGroups,
  dipssPlusRiskGroups,
  calculateDipss,
  calculateDipssPlus,
} from '../../data/diseasesData';
import { useUserInput } from '../../context/UserInputContext';

const DipssCalculator: React.FC = () => {
  const { state, update } = useUserInput();
  const dipss = state.dipss;
  const plus = state.dipssPlus;

  const setDipss = (val: typeof dipss) => update('dipss', val);
  const setPlus = (val: typeof plus) => update('dipssPlus', val);

  const dipssResult = useMemo(() => {
    const age = parseFloat(dipss.age);
    const wbc = parseFloat(dipss.wbc);
    const hb = parseFloat(dipss.hemoglobin);
    const blasts = parseFloat(dipss.blasts);
    const symptoms = dipss.constitutionalSymptoms;

    if (isNaN(age) || isNaN(wbc) || isNaN(hb) || isNaN(blasts) || symptoms === null) {
      return null;
    }
    return calculateDipss(age, wbc, hb, blasts, symptoms);
  }, [dipss]);

  const dipssPlusResult = useMemo(() => {
    if (!dipssResult) return null;
    if (plus.plateletsLow === null || plus.transfusionNeed === null || plus.unfavorableKaryotype === null) {
      return null;
    }
    return calculateDipssPlus(
      dipssResult.level,
      plus.plateletsLow,
      plus.transfusionNeed,
      plus.unfavorableKaryotype
    );
  }, [dipssResult, plus]);

  const inputs = (
    <>
      <NumberField
        label="Age (years)"
        value={dipss.age}
        onChange={(v) => setDipss({ ...dipss, age: v })}
        placeholder="e.g. 70"
        min={0}
        max={120}
      />
      <NumberField
        label={<>WBC (×10⁹/L)</>}
        value={dipss.wbc}
        onChange={(v) => setDipss({ ...dipss, wbc: v })}
        placeholder="e.g. 30"
        min={0}
        step={0.1}
        decimal
      />
      <NumberField
        label="Hemoglobin (g/dL)"
        value={dipss.hemoglobin}
        onChange={(v) => setDipss({ ...dipss, hemoglobin: v })}
        placeholder="e.g. 9.5"
        min={0}
        step={0.1}
        decimal
      />
      <NumberField
        label="Peripheral blood blasts (%)"
        value={dipss.blasts}
        onChange={(v) => setDipss({ ...dipss, blasts: v })}
        placeholder="e.g. 2"
        min={0}
        max={100}
        step={0.1}
        decimal
      />
      <YesNoToggle
        label="Constitutional symptoms"
        value={dipss.constitutionalSymptoms}
        onChange={(v) => setDipss({ ...dipss, constitutionalSymptoms: v })}
        hint="Weight loss >10% over 6 months, night sweats, or unexplained fever >37.5°C"
      />
    </>
  );

  const aside = dipssResult ? (
    <div className="score-display">
      <span className="score-label">DIPSS Score</span>
      <span className="score-value">{dipssResult.score}</span>
    </div>
  ) : null;

  return (
    <div className="dipss-combined">
      <CalcShell
        title="DIPSS — Dynamic International Prognostic Scoring System"
        subtitle="5 variables; Hb <10 g/dL counts for 2 points"
        inputs={inputs}
        aside={aside}
      >
        <RiskScoreResult
          groups={dipssRiskGroups}
          activeLevel={dipssResult?.level || null}
          emptyHint="Fill in all fields above to calculate DIPSS"
        />
      </CalcShell>

      {dipssResult && (
        <div className="dipss-plus-section">
          <h4 className="dipss-plus-section__title">
            DIPSS-Plus — Additional Variables
          </h4>
          <p className="dipss-plus-section__subtitle">
            DIPSS result ({dipssResult.level.toUpperCase()}) is used as the base score
          </p>

          <div className="dipss-plus-section__inputs">
            <YesNoToggle
              label={<>Platelets &lt;100 × 10⁹/L</>}
              value={plus.plateletsLow}
              onChange={(v) => setPlus({ ...plus, plateletsLow: v })}
            />
            <YesNoToggle
              label="RBC transfusion need"
              value={plus.transfusionNeed}
              onChange={(v) => setPlus({ ...plus, transfusionNeed: v })}
            />
            <YesNoToggle
              label="Unfavorable karyotype"
              value={plus.unfavorableKaryotype}
              onChange={(v) => setPlus({ ...plus, unfavorableKaryotype: v })}
              hint="complex or sole or two abnormalities that include trisomy +8, -7/7q-, i(17q), -5/5q-, 12p-, inv(3), 11q23 rearrangement"
            />
          </div>

          {dipssPlusResult && (
            <div className="dipss-plus-section__result">
              <div className="score-display">
                <span className="score-label">DIPSS-Plus Score</span>
                <span className="score-value">{dipssPlusResult.score}</span>
              </div>
            </div>
          )}

          <RiskScoreResult
            groups={dipssPlusRiskGroups}
            activeLevel={dipssPlusResult?.level || null}
            emptyHint="Answer all 3 questions above to calculate DIPSS-Plus"
          />
        </div>
      )}
    </div>
  );
};

export default DipssCalculator;
